import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SkillDisplayNamePipe } from '../../pipes/skill-display-name.pipe';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as fromApp from '../../../store/app.reducer';
import * as CompanyActions from '../../../components/company/store/company.actions';
import { CustomerModel } from '../../../models/customer.model';
import { EmployeeModel } from '../../../models/employee.model';
import { environment } from '../../../../environments/environment';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';
import { customEmailValidator } from '../../validators/email.validator';
import * as SharedActions from '../../../shared-store/shared.actions'

@Component({
    selector: 'app-invite-coworkers-modal',
    templateUrl: './invite-coworkers-modal.component.html',
    styleUrls: ['./invite-coworkers-modal.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        TranslateModule,
        SkillDisplayNamePipe
    ]
})

export class InviteCoworkersModalComponent implements OnInit {
    public subscription: Subscription;
    inviteForm: FormGroup;
    emailForms: FormGroup[] = [];
    appUrl = environment.appUrl;
    public customer: CustomerModel;
    public employee: EmployeeModel;
    skills: any;
    private unsubscribe$ = new Subject<void>();
    selectedSkillIds: number[] = [];
    isAllSelected = false;
    isAllSelectedPlace = false;
    jobPlaces: any[] = [];
    selectedJobPlaceIds: number[] = [];
    groupedJobPlaces: any[] = [];
    jobPlaceIds: number[] = [];
    isToggled = false;
    employeeRoleId = 3;

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<InviteCoworkersModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private store: Store<fromApp.AppState>,
        private translate: TranslateService
    ) {
        this.inviteForm = this.fb.group({});
        this.addEmailField();
    }

    createEmailFormGroup(): FormGroup {
        return this.fb.group({
            email: ['', [Validators.required, customEmailValidator()]],
            employeeRoleId: [3]
        });
    }

    getErrorMessage(index: number): string {
        const emailForm = this.emailForms[index];
        if (!emailForm) return '';

        const emailControl = emailForm.get('email');
        if (!emailControl) return '';

        if (emailControl.hasError('required')) {
            return this.translate.instant('company.invite.error_required');
        }
        if (emailControl.hasError('email')) {
            return this.translate.instant('company.invite.error_invalid_email');
        }
        return '';
    }

    ngOnInit(): void {
        this.subscription = this.store.subscribe(appState => {
            this.customer = appState.auth.customer;
            this.employee = appState.auth.employee;
        });

        this.store.dispatch(new SharedActions.LoadSkills({ customerId: this.customer.id }));
        this.store.select(state => state.shared.skills)
            .pipe(
                takeUntil(this.unsubscribe$),
                filter(skills => skills && skills.length > 0)
            )
            .subscribe((skills: any) => {
                this.skills = skills;
            });

        this.store.dispatch(new CompanyActions.LoadObjects({ customerId: this.customer.id }));
        this.store
            .select((state) => state.company.objects)
            .pipe(
                takeUntil(this.unsubscribe$),
                filter((data) => !!data && data.length > 0)
            )
            .subscribe((data) => {
                this.jobPlaces = data;
            });

        this.store
            .select(state => state.company.objects)
            .pipe(filter(data => !!data && data.length > 0))
            .subscribe((jobPlaces) => {
                this.groupedJobPlaces = this.groupJobPlacesByState(jobPlaces);
            });
        this.inviteForm = this.fb.group({
            employeeRoleId: [3]
        });
    }

    /** Group job places by state and sort by count descending */
    groupJobPlacesByState(jobPlaces: any[]) {
        const grouped: any = {};

        for (const place of jobPlaces) {
            const stateId = place.state?.id;
            if (!grouped[stateId]) {
                grouped[stateId] = {
                    stateId,
                    stateName: place.state?.name,
                    places: []
                };
            }
            grouped[stateId].places.push(place);
        }
        const groupedArray = Object.values(grouped);
        groupedArray.sort((a: any, b: any) => b.places.length - a.places.length);
        return groupedArray;
    }

    getEmailName(email: string) {
        return email.split('@')[0].split('.')[0];
    }

    addEmailField() {
        const newEmailForm = this.createEmailFormGroup();
        this.emailForms.push(newEmailForm);
    }

    removeEmailField(index: number) {
        this.emailForms.splice(index, 1);
    }

    isFormValid(): boolean {
        return this.emailForms.every(form => form.valid) && this.emailForms.length > 0;
    }

    onSubmit() {
        if (this.isFormValid()) {
            const validEmails = this.emailForms
                .map(form => form.get('email')?.value)
                .filter(email => email && email.trim() !== '');

            if (validEmails.length > 0) {
                const inviteData = validEmails.map(email => ({
                    email: email,
                    employeeRoleId: this.inviteForm.get('employeeRoleId')?.value || 3,
                    firstName: this.getEmailName(email),
                    jobplaceIds: this.jobPlaceIds || [],
                    skillIds: this.selectedSkillIds || [],
                }));

                this.store.dispatch(new CompanyActions.InviteUsers({
                    employee: this.data.employee.id,
                    customer: this.data.customer.id,
                    data: inviteData
                }));

                this.dialogRef.close();
            }
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    /** Handle individual checkbox change */
    onSkillChange(skillId: number, event: any): void {
        if (event.target.checked) {
            if (!this.selectedSkillIds.includes(skillId)) {
                this.selectedSkillIds.push(skillId);
            }
        } else {
            this.selectedSkillIds = this.selectedSkillIds.filter(id => id !== skillId);
        }
         this.isAllSelected = this.selectedSkillIds.length === this.skills.length;
    }

    /** Allow clicking on label to also toggle */
    onSkillLabelClick(skillId: number): void {
        if (this.selectedSkillIds.includes(skillId)) {
            this.selectedSkillIds = this.selectedSkillIds.filter(id => id !== skillId);
        } else {
            this.selectedSkillIds.push(skillId);
        }
        this.isAllSelected = this.selectedSkillIds.length === this.skills.length;
    }

    /** Select/Deselect all */
    toggleSelectAll(): void {
        if (this.isAllSelected) {
            this.selectedSkillIds = [];
            this.isAllSelected = false;
        } else {
            this.selectedSkillIds = this.skills.map(skill => skill.id);
            this.isAllSelected = true;
        }
    }

    // Helper to display count
    get selectedSkillCount(): number {
        return this.selectedSkillIds.length;
    }

    get selectedJobPlaceCount(): number {
        return this.jobPlaceIds.length;
    }

    /** Toggle one job place */
    toggleJobPlaceSelection(placeId: number) {
        const index = this.jobPlaceIds.indexOf(placeId);
        if (index === -1) {
            this.jobPlaceIds.push(placeId);
        } else {
            this.jobPlaceIds.splice(index, 1);
        }
    }

    /** Select / deselect all job places globally */
    selectAllJobPlaces() {
        if (this.isAllSelectedPlace) {
            this.selectedSkillIds = [];
            this.isAllSelected = false;
        }
        const allIds = this.groupedJobPlaces.flatMap(g => g.places.map(p => p.id));
        const allSelected = allIds.every(id => this.jobPlaceIds.includes(id));
            this.jobPlaceIds = allSelected ? [] : allIds;
    }


    /** Handle individual job place click (checkbox or label) */
    onJobPlaceLabelClick(jobPlaceId: number) {
        if (this.jobPlaceIds.includes(jobPlaceId)) {
            this.jobPlaceIds = this.jobPlaceIds.filter(id => id !== jobPlaceId);
        } else {
            this.jobPlaceIds.push(jobPlaceId);
        }
    }

    /** Select / deselect all job places within a specific state */
    toggleStateSelection(group: any) {
        const statePlaceIds = group.places.map(p => p.id);
        const allSelected = statePlaceIds.every(id => this.jobPlaceIds.includes(id));

        if (allSelected) {
            // Deselect all in this state
            this.jobPlaceIds = this.jobPlaceIds.filter(id => !statePlaceIds.includes(id));
        } else {
            // Select all in this state
            const newIds = statePlaceIds.filter(id => !this.jobPlaceIds.includes(id));
            this.jobPlaceIds = [...this.jobPlaceIds, ...newIds];
        }
    }

    /** Check if all job places are selected */
    areAllJobPlacesSelected(): boolean {
        const allIds = this.groupedJobPlaces.flatMap(g => g.places.map(p => p.id));
        return allIds.every(id => this.jobPlaceIds.includes(id));
    }
    // optional helper
    onToggleChange(event: any) {
        const isChecked = event.target.checked;
        this.inviteForm.get('employeeRoleId')?.setValue(isChecked ? 1 : 3);
    }

    onCancel() {
        this.dialogRef.close();
    }
} 